import { registerPlugin } from "@capacitor/core";

export type NativeMovementRecordingError = {
  accountId: string;
  clientId: string;
  message: string;
  at: number;
};

/** A movement session as the native engine sees it. */
export type NativeMovementSession = {
  accountId?: string;
  clientId?: string;
  segmentId?: number;
  lastControlAtMs?: number;
  endedAtMs?: number;
  pausedAtMs?: number;
  disposition?: "completed" | "discarded";
  error?: string;
  active: boolean;
  activityType?: string;
  title?: string;
  startedAtMs?: number;
  pausedAccumMs?: number;
  isPaused?: boolean;
  distanceMeters?: number;
  elapsedSeconds?: number;
  paceSecondsPerKm?: number;
  speedMps?: number;
  /** True when the Apple Watch started this session and the web layer has not
   * caught up with it yet. */
  startedFromWatch?: boolean;
  /** GPS profiles this binary's native filter understands (Android). Absent
   * on builds that predate the "auto" profile. */
  gpsProfiles?: string[];
};

// Native iOS movement engine: owns its own CLLocationManager so distance, pace,
// splits and the elapsed timer keep updating while backgrounded/locked (the
// WebView JS is suspended then), and publishes every one of those frames to the
// Apple Watch. Replaces capacitor-live-activity for movement.
type HiiveMovementActivityPlugin = {
  isAvailable(): Promise<{ value: boolean }>;
  setAccount(input: { accountId: string | null }): Promise<void>;
  getLocations(input: {
    accountId: string;
    clientId: string;
  }): Promise<{ locations: import("@/lib/movement/gps-utils").GPSPoint[] }>;
  acknowledgeSession(input: {
    accountId: string;
    clientId: string;
  }): Promise<void>;
  addListener(
    event: "recordingError",
    callback: (error: NativeMovementRecordingError) => void,
  ): Promise<import("@capacitor/core").PluginListenerHandle>;
  addListener(
    event: "location",
    callback: (
      point: import("@/lib/movement/gps-utils").GPSPoint & {
        accountId?: string;
        clientId?: string;
      },
    ) => void,
  ): Promise<import("@capacitor/core").PluginListenerHandle>;
  start(input: {
    accountId: string;
    clientId: string;
    activityType: string;
    title?: string;
    startedAtMs: number;
    /** "auto": GPS rules for a recording whose type is decided at save. Not
     * part of the recording identity; older binaries ignore it. */
    gpsProfile?: "auto";
    /** Target distance for the Live Activity's goal bar. Only set when the run
     * is following a saved route — `mvRoute.distanceMeters` is the only
     * distance target the product has. */
    goalDistanceMeters?: number;
  }): Promise<void>;
  getSession(input?: {
    accountId: string;
    clientId: string;
  }): Promise<NativeMovementSession>;
  update(input: {
    controlAtMs: number;
    accountId: string;
    clientId: string;
    distanceMeters?: number;
    paceSecondsPerKm?: number;
    speedMps?: number;
    isPaused?: boolean;
    title?: string;
  }): Promise<void>;
  setPaused(input: {
    controlAtMs: number;
    accountId: string;
    clientId: string;
    isPaused: boolean;
  }): Promise<void>;
  stop(input?: {
    accountId?: string;
    clientId?: string;
    endedAtMs?: number;
    completed?: boolean;
  }): Promise<void>;
};

export const HiiveMovementActivity =
  registerPlugin<HiiveMovementActivityPlugin>("HiiveMovementActivity");
